import React, { useEffect, useState } from "react";
import { TabContext, TabList } from "@mui/lab";
import { Alert, Box, Button, Stack, Tab, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { i18n } from "i18next";
import { DeleteForever } from "@mui/icons-material";
import { setDocumentTitle } from "system/Utilities";

import TopBar from "components/dashboardParts/TopBar";
import { currentUser, InfegyAccount, InfegyUser, permissionTest } from "system/InfegyUser";
import UserUsage from "./tabs/UserUsage";
import AccountUsage from "./tabs/AccountUsage";
import PrePurchaseUsage from "./tabs/PrePurchaseUsage";
import PayAsYouGoUsage from "./tabs/PayAsYouGoUsage";
import Invoices from "./tabs/Invoices";
import SelectUserButton from "components/uiElements/inputs/buttons/SelectUserButton";
import APIRequest from "system/APIRequest";
import { Usage } from "./types/Usage";
import { BillingOrder } from "./types/BillingOrder";
import { TextLoader } from "components/apiHandling/TextLoader";

dayjs.extend(quarterOfYear);

enum ApiUsageTab {
  Account = "account",
  Invoices = "invoices",
  PayAsYouGo = "pay-as-you-go",
  PrePurchase = "pre-purchase",
  User = "user"
}

export type UsageQueryParams = {
  account_id?: string;
  // all_users?: boolean;
  // quota?: boolean;
  since?: string;
  token_id?: string;
  // token_type?: "api" | "web";
  until?: string;
  user_id?: string;
}

export interface UsageTerm {
  term_end?: string;
  term_start: string;
}

export enum UsageTermType {
  CurrentSubscriptionMTD = "current_subscription_mtd",
  CurrentSubscriptionYTD = "current_subscription_ytd",
  Last30Days = "last_30_days",
  LastQuarter = "last_quarter",
  LastYear = "last_year"
}

export interface UsageTermOption {
  label: string;
  type: UsageTermType;
}

export enum UsageChartType {
  AccumulatedUsage,
  PeriodicUsage
}

export type UsageDisplayMetric = "cost" | "count" | "price";

export function getUsageTermOptions(i18n: i18n): Array<UsageTermOption> {
  return [{
    label: i18n.t(`usage.terms.${UsageTermType.CurrentSubscriptionMTD}`),
    type: UsageTermType.CurrentSubscriptionMTD
  }, {
    label: i18n.t(`usage.terms.${UsageTermType.CurrentSubscriptionYTD}`),
    type: UsageTermType.CurrentSubscriptionYTD
  }, {
    label: i18n.t(`usage.terms.${UsageTermType.Last30Days}`),
    type: UsageTermType.Last30Days
  }, {
    label: i18n.t(`usage.terms.${UsageTermType.LastQuarter}`),
    type: UsageTermType.LastQuarter
  }, {
    label: i18n.t(`usage.terms.${UsageTermType.LastYear}`),
    type: UsageTermType.LastYear
  }];
}

export function getUsageTerm(usageTermType: UsageTermType, currentTerm: UsageTerm): UsageTerm {
  const now = dayjs();
  const nowUtc = now.utc();

  switch (usageTermType) {
    case UsageTermType.CurrentSubscriptionMTD:
    default: {
      // The CurrentSubscriptionMTD is an up to 30-day window that starts and resets on the
      // monthly date that service starts on.  For example, if the service start date was 
      // January 15th, this date should always be on the 15th of each month.
      // Start with the current subscription start date.
      let subscriptionStart = dayjs(currentTerm.term_start);
      // Align the date with the current month and year.
      subscriptionStart = subscriptionStart.set("month", nowUtc.month());
      subscriptionStart = subscriptionStart.set("year", nowUtc.year());
      if (subscriptionStart > nowUtc) {
        // If the alignment pushes the date into the future, set the date to the previous month.
        subscriptionStart = subscriptionStart.subtract(1, "month");
      }
      return {
        term_start: subscriptionStart.toISOString()
      }
    }
    case UsageTermType.CurrentSubscriptionYTD: {
      // The CurrentSubscriptionYTD is a 365-day window that starts with the service start date.
      // Start with the current subscription start date.
      let subscriptionStart = dayjs(currentTerm.term_start);
      const diff = nowUtc.diff(subscriptionStart, "year");
      if (diff > 0) {
        // If the subscription start date is over a year ago, cap it to one year ago from today.
        subscriptionStart = nowUtc.subtract(1, "year");  
      }
      return {
        term_start: currentTerm.term_start
      }
    }
    case UsageTermType.Last30Days:
      return {
        term_end: nowUtc.toISOString(),
        term_start: nowUtc.subtract(30, "days").toISOString()
      }
    case UsageTermType.LastQuarter:
      return {
        term_end: nowUtc.toISOString(),
        term_start: nowUtc.subtract(1, "quarter").toISOString()
      }
    case UsageTermType.LastYear:
      return {
        term_end: nowUtc.toISOString(),
        term_start: nowUtc.subtract(1, "year").toISOString()
      }
  }
}

export async function getAccountUsage(usageTerm: UsageTerm, accountId: string): Promise<Usage> {
  const queryParams: UsageQueryParams = {
    account_id: accountId,
    since: usageTerm.term_start,
    until: usageTerm.term_end
  };
  const response: Usage = await APIRequest("usage", queryParams) as Usage;
  return response;
}

export async function getBillingOrder(billingOrderId: string, admin: boolean): Promise<BillingOrder> {
  const adminParam = admin ? "?admin=1" : ""
  const response: any = await APIRequest(`obj/billing-order/${billingOrderId}${adminParam}`); // &minimal=1
  return response.output;
}

export default function ApiUsagePage(): React.JSX.Element {
  const { i18n } = useTranslation();
  const thisUser = currentUser();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedUser, setSelectedUser] = useState<InfegyUser | null>(null);
  const [selectedUserAccount, setSelectedUserAccount] = useState<InfegyAccount | null>(null);
  const [availableTabs, setAvailableTabs] = useState<Array<ApiUsageTab>>([]);
  const [activeTab, _setActiveTab] = useState<ApiUsageTab>(ApiUsageTab.User);
  const [loadedTabs, setLoadedTabs] = useState<Partial<Record<ApiUsageTab, boolean>>>({});

  const [usageTerm, setUsageTerm] = useState<UsageTerm | null>(null);

  const activeAccount = selectedUserAccount || thisUser?.account;

  useEffect(() => {
    setDocumentTitle(i18n.t("api-usage-page.title"));
  }, []);

  useEffect(() => {
    if (!thisUser?.admin && !permissionTest("api")) {
      // The user has insufficient permissions to view this page.
      navigate("start");
    }
  }, []);

  function setActiveTab(apiUsageTab: ApiUsageTab) {
    loadedTabs[apiUsageTab] = true;
    _setActiveTab(apiUsageTab);
  }

  function buildAvailableTabs(user: InfegyUser): Array<ApiUsageTab> {
    const result = [
      ApiUsageTab.User
    ];
    if (permissionTest("account_admin")) {
      result.push(ApiUsageTab.Account);
      result.push(ApiUsageTab.PrePurchase);
      result.push(ApiUsageTab.PayAsYouGo);
      result.push(ApiUsageTab.Invoices);
    }
    return result;
  }

  async function getAccount(accountId: string): Promise<InfegyAccount> {
    const adminParam = accountId ? "?admin=1" : "";
    const response: any = await APIRequest(`obj/account/${accountId}${adminParam}`);
    return response.output;
  }

  async function getUsageTerm(user?: InfegyUser | null): Promise<UsageTerm> {
    const queryParams: UsageQueryParams = {
      since: "now",
      until: "now",
    };
    if (user) {
      queryParams.user_id = user.id;
    }
    const response: Usage = await APIRequest("usage", queryParams) as Usage;
    return {
      term_end: response.term_end,
      term_start: response.term_start
    }
  }

  async function load(selectedUser?: InfegyUser | null, changeTab?: ApiUsageTab) {
    const user = selectedUser || thisUser;
    if (!user) {
      return;
    }

    setIsLoading(true);

    const tabs = buildAvailableTabs(user);

    let account: InfegyAccount | null = null;
    if (selectedUser) {
      account = await getAccount(selectedUser.account_id);
    }

    const usageTerm = await getUsageTerm(selectedUser);

    setSelectedUser(selectedUser || null);
    setSelectedUserAccount(account);
    setAvailableTabs(tabs);
    const newActiveTab = tabs.find((currentTab) => currentTab === changeTab)
      || tabs.find((currentTab) => currentTab === activeTab)
      || ApiUsageTab.User;
    setActiveTab(newActiveTab);
    setLoadedTabs({[newActiveTab]: true});
    setUsageTerm(usageTerm);

    setIsLoading(false);
  }

  async function handleSelectUser(userId: string) {
    const response: any = await APIRequest(`obj/user/${userId}`, {admin: "1"});
    const selectedUser: InfegyUser | undefined = response.output;
    load(selectedUser, ApiUsageTab.User);
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <Box sx={{ maxWidth: "1300px", margin: "0 auto", position: "relative", width: "100%" }}>
      <TopBar showLogo={true} title={i18n.t("api-usage-page.title")} />
      <Stack sx={{
        gap: 1
      }}>
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: "center",
            justifyContent: "flex-end"
          }}>
          <Box sx={{
            flex: 1
          }}>
            <Alert severity="info" sx={{ height: "100%" }} variant="outlined" >
              {i18n.t("api-usage-page.utc-info")}
            </Alert>
          </Box>
          {(thisUser?.admin || selectedUser) &&
            <>
              <Typography variant="h4">{i18n.t("api-usage-page.selected-user-label")}</Typography>
              <SelectUserButton
                disabled={!thisUser?.admin}
                onChange={(user) => load(user)}
                user={selectedUser}
              />
              {!!selectedUser &&
                <Button
                  color="error"
                  onClick={() => load()}
                  variant="outlined"
                >
                  <DeleteForever />
                </Button>
              }
            </>
          }
        </Stack>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList sx={{display: "inline"}} onChange={(event, value: ApiUsageTab) => setActiveTab(value)}>
              {availableTabs.map((value: ApiUsageTab): React.JSX.Element => {
                return <Tab key={value} label={i18n.t(`api-usage-page.tabs.${value}`)} value={value} />
              })}
            </TabList>
          </Box>
          {isLoading &&
            <TextLoader />
          }
          {!isLoading && usageTerm &&
            <>
              {loadedTabs[ApiUsageTab.User] &&
                <UserUsage currentTerm={usageTerm} user={selectedUser} visible={activeTab === ApiUsageTab.User} />
              }
              {loadedTabs[ApiUsageTab.Account] && activeAccount &&
                <AccountUsage account={activeAccount} currentTerm={usageTerm} onSelectUser={handleSelectUser} visible={activeTab === ApiUsageTab.Account} />
              }
              {loadedTabs[ApiUsageTab.PrePurchase] && activeAccount &&
                <PrePurchaseUsage account={activeAccount} currentTerm={usageTerm} visible={activeTab === ApiUsageTab.PrePurchase} />
              }
              {loadedTabs[ApiUsageTab.PayAsYouGo] && activeAccount &&
                <PayAsYouGoUsage account={activeAccount} currentTerm={usageTerm} visible={activeTab === ApiUsageTab.PayAsYouGo} />
              }
              {loadedTabs[ApiUsageTab.Invoices] && activeAccount &&
                <Invoices account={activeAccount} visible={activeTab === ApiUsageTab.Invoices} />
              }
            </>
          }
        </TabContext>
      </Stack>
    </Box>
  );
}
